/**
 * @description Component-OrderBookSection
 */
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { WrappedPair } from '@/entities/WrappedPair';
import { useChainId } from '@/hooks/web3/useChain';

import OrderBook from '@/pages/trade/CoinPriceChart/OrderBook';

import { getImrStepRatio } from '@/constants/trade';
import { useTradeStore } from '@/features/trade/store';
import { useResponsiveTradeWidth } from '@/hooks/useResponsiveTradeWidth';
import StepRatioSelector from '@/pages/trade/CoinPriceChart/StepRatioSelector';
import SymbolSelector from '@/pages/trade/CoinPriceChart/SymbolSelector';
import { useMediaQueryDevice } from '../MediaQuery';
import './index.less';

interface OrderBookSectionProps {
  currentPair?: WrappedPair;
  className?: string;
}

const OrderBookSection: FC<OrderBookSectionProps> = ({ currentPair, className = '' }) => {
  const { t } = useTranslation();
  const chainId = useChainId();
  const { style: wrapperStyle } = useResponsiveTradeWidth(280, 420);
  const pairKey = useMemo(() => currentPair?.id, [currentPair?.id]);
  const orderbookUnitBase = useTradeStore((state) => state.orderbookUnitBase);
  const setOrderBookUnitBase = useTradeStore((state) => state.setOrderBookUnitBase);
  const setPairStepRatio = useTradeStore((state) => state.setPairStepRatio);
  const stepRatio = useTradeStore((state) => state.stepRatio);
  const { isMobile } = useMediaQueryDevice();
  const imr = currentPair?.rootInstrument.metaFutures.setting.initialMarginRatio;

  useEffect(() => {
    if (!pairKey || !chainId || !imr) return;
    const validOptions = getImrStepRatio(imr, chainId);
    const { pairStepRatioMap, stepRatio: currentStepRatio } = useTradeStore.getState();
    const saved = pairStepRatioMap[pairKey];
    if (saved !== undefined && validOptions.includes(saved)) {
      setPairStepRatio(pairKey, saved);
    } else if (!validOptions.includes(currentStepRatio)) {
      setPairStepRatio(pairKey, validOptions[0]);
    }
  }, [pairKey, chainId, imr]);

  return (
    <div
      className={`orderbook-section-wrapper ${className}`}
      style={isMobile ? { width: '100%', maxWidth: 'unset' } : wrapperStyle}>
      <div className="orderbook-section">
        <div className="orderbook-section-top">
          {!isMobile && (
            <div className="orderbook-section-title">
              <span className="orderbook-section-title-text">{t('common.orderBook.orderB')}</span>
            </div>
          )}
          <div className="orderbook-section-top-selectors">
            <StepRatioSelector
              stepRatio={stepRatio}
              dropdownAlign={{ points: ['tr', 'br'], offset: [0, 4] }}
              popupMatchSelectWidth={false}
              popupClassName="orderbook-section-top-selectors-step-ratio-popup"
              onChange={(ratio) => {
                pairKey && setPairStepRatio(pairKey, ratio);
              }}
              imr={currentPair?.rootInstrument.metaFutures.setting.initialMarginRatio}
            />
            <SymbolSelector
              isBase={orderbookUnitBase}
              onChange={setOrderBookUnitBase}
              baseToken={currentPair?.rootInstrument.baseToken}
              quoteToken={currentPair?.rootInstrument.quoteToken}
            />
          </div>
        </div>
        <div className="orderbook-section-container orderbook">
          <OrderBook stepRatio={stepRatio} pair={currentPair} chainId={chainId} />
        </div>
      </div>
    </div>
  );
};

export default OrderBookSection;
